import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { v4 as uuid } from 'uuid';

import {
  HStack,
  Icon,
  NewButton,
  Paragraph,
  useToast,
} from '@eluve/components';
import {
  getUserCompletedAppointmentsCountQuery,
  insertTranscriptMutation,
} from '@eluve/frontend-appointment-hooks';
import { useUserIdFromSession } from '@eluve/session-helpers';
import { useUserSpecialty } from '@eluve/smart-blocks';
import { SampleTranscriptsBySpecialty } from '@eluve/utils';

const DefaultTranscript = SampleTranscriptsBySpecialty['General Practice'];

interface SampleTranscriptProviderProps {
  appointmentId: string;
  tenantId: string;
}

export const SampleTranscriptProvider: React.FC<
  SampleTranscriptProviderProps
> = ({ appointmentId, tenantId }) => {
  const toast = useToast();
  const [addedSuccessfully, setAddedSuccessfully] = useState(false);
  const [insertTranscript, { loading: inserting }] = useMutation(
    insertTranscriptMutation,
  );

  const userId = useUserIdFromSession();
  const { specialty } = useUserSpecialty(userId);

  const { data, loading } = useQuery(getUserCompletedAppointmentsCountQuery, {
    variables: { tenantId, userId },
  });

  const hasCompletedAppointments =
    (data?.appointmentsAggregate?.aggregate?.count ?? 0) > 0;

  if (
    addedSuccessfully ||
    !DefaultTranscript ||
    loading ||
    hasCompletedAppointments
  ) {
    return null;
  }

  const handleOnClick = async () => {
    let transcript = DefaultTranscript;
    if (specialty && specialty in SampleTranscriptsBySpecialty) {
      const specialtyTranscript = SampleTranscriptsBySpecialty[specialty];
      if (specialtyTranscript) {
        transcript = specialtyTranscript;
      }
    }

    const segmentId = uuid();

    await insertTranscript({
      variables: { tenantId, appointmentId, transcript, segmentId },
      onCompleted: () => setAddedSuccessfully(true),
      onError: () => toast.error('Sample transcript could not be added.'),
    });
  };

  return (
    <HStack
      className="mb-4 rounded-lg bg-bg-secondary p-2 pl-3.5"
      justify="between"
    >
      <HStack gap={3}>
        <Icon name="Info" size="xs" />
        <Paragraph size="md" color="primary" weight="regular">
          Can't record an appointment right now? Try a sample transcript to see
          how Eluve works!
        </Paragraph>
      </HStack>
      <NewButton
        loading={inserting}
        onClick={handleOnClick}
        size="s"
        text="Try a transcript"
        type="outline"
      />
    </HStack>
  );
};
